import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import styles from './filters-element-main.module.scss';
import { useServices } from '@/services-context';
import { useProductArea } from '../context';
import { CurrencyConverter } from '@/util/currency-converter';
import FixCheckbox from '@/elements-react/fix-checkbox';
import FixButtonDefault from '@/elements-react/fix-button-default';
import { LayoutStyleTypes } from "@/util-react/layout";
import { Key } from '../store/store';
import { PARAMETERS_LISTENER_NAME } from "@/services/parameters.ts";
import { Gtag } from "@/services/gtag.ts";
import { googleEvent } from "@/helpers/google-events.ts";

const FiltersElementMain = (props: { type: LayoutStyleTypes.TYPE_LG | LayoutStyleTypes.TYPE_SM, group: Key }) => {
  const { type } = props;
  const { t } = useTranslation();
  const services = useServices([PARAMETERS_LISTENER_NAME]);
  const area = useProductArea();
  const currencySymbol = CurrencyConverter.getCurrencySymbol(services.parameters.getVisibleCurrency());
  const groupKey = props.group;
  const group = area.groups[groupKey]!;

  const hasBrands = area.options.brands.length > 0;
  const hasCategories = area.options.categories.length > 0;
  const hasBoth = hasBrands && hasCategories;

  const renderCheckboxes = useCallback((params: {
    name?: string;
    search?: boolean;
    searchText: string;
    onChange: (key: string) => void;
    items: Array<{ key: string; name: string; selected: boolean }>;
    onSearch: (text: string) => void;
  }) => (
    <div className={classNames(styles.section, styles.checkboxes)}>
      {params.name && <div className={styles.title}>{params.name}</div>}
      {params.search && (
        <input
          className={styles.search}
          placeholder={t('0x619y4k02smw9cs.search', 'Search')}
          value={params.searchText}
          onChange={e => params.onSearch((e.target as HTMLInputElement).value)}
        />
      )}
      <div className={styles.list}>
        {params.items.map(item => (
          <FixCheckbox name={item.name} key={item.key} checked={item.selected} onChange={() => params.onChange(item.key)}>
            {item.name}
          </FixCheckbox>
        ))}
      </div>
    </div>
  ), [t]);

  const renderClear = (onClear: () => void) => (
    <FixButtonDefault className={styles.clear} onClick={onClear}>
      {t('38xtmz11ad14blsh.clear-all', 'Clear All')}
    </FixButtonDefault>
  );

  const renderGap = (gapType: 'x1' | 'x2' | 'x2line' | 'x05') => (
    <div className={classNames(styles.gap, styles[gapType])} />
  );

  const renderBrandCheckboxes = () =>{
    const items = area.getFilteredOptions(groupKey, 'brands').map(brand => ({
      key: brand.key,
      name: brand.name,
      selected: group.selections.has(brand.key),
    }));

    const filtered = items.map((item) => item.name);
    const applied = items.filter((item) => item.selected).map((item) => item.name);

    return renderCheckboxes({
      name: t('v0vi8228d2e6gbge.brand', 'Brand'),
      search: true,
      searchText: group.searchs.brand,
      items,
      onSearch: (text) => {
        area.changeSearch(groupKey, 'brands', text);
        Gtag.clearData();
        Gtag.pushEvent("brand_filter_search", { filtered, searchText: text, filter: "" });
      },
      onChange: (key) => {
        area.toggleSelection(groupKey, key);
        Gtag.pushEvent("brand_filter_applied", { filter: { selected_brands: applied }, filtered: "" });
      },
    });
  };


  const renderCategoryCheckboxes = () => {
    const items = area.getFilteredOptions(groupKey, 'categories').map(category => ({
      key: category.key,
      name: category.name,
      selected: group.selections.has(category.key),
    }));

    const filtered = items.map((item) => item.name);
    const applied = items.filter((item) => item.selected).map((item) => item.name);

    return renderCheckboxes({
      name: t('83ao1kah0uewlr1m.categories', 'Categories'),
      search: true,
      searchText: group.searchs.category,
      items,
      onSearch: (text) => {
        area.changeSearch(groupKey, 'categories', text);
        Gtag.pushEvent("categories_filter_search", { searchText: text, filtered, filter: "" });
      },
      onChange: (key) => {
        area.toggleSelection(groupKey, key);
        Gtag.pushEvent("categories_filter_applied", { filter: { selected_categories: applied, filtered: "" } });
      },
    });
  };

  const renderPriceRange = () => {
    const { from, to } = group.price;

    return (
      <div className={classNames(styles.section, styles.range)}>
        <div className={styles.title}>{t('r8yxt597wde4kw5n.price', 'Price')}</div>
        <div className={styles.inputs}>
          <input
            className={styles.input}
            placeholder={t('5jofq90us8x35pvo.min', '{{code}} min', { code: currencySymbol })}
            value={from}
            onChange={e => area.changePrice(groupKey, 'from', e.currentTarget.value, e.currentTarget)}
            onInput={() => googleEvent("price_filter_applied", { from, to })}
            type="text"
            inputMode="numeric"
          />
          <div className={styles.msg}>{t('562j01i4db4j6mua.to', 'to')}</div>
          <input
            className={styles.input}
            placeholder={t('mqxa4h3tzcd8wz17.max', '{{code}} max', { code: currencySymbol })}
            value={to}
            onChange={e => area.changePrice(groupKey, 'to', e.currentTarget.value, e.currentTarget)}
            onInput={() => googleEvent("price_filter_applied", { from, to })}
            type="text"
            inputMode="numeric"
          />
        </div>
      </div>
    );
  };

  return (
    <div className={styles.container}>
      {renderPriceRange()}
      {type === LayoutStyleTypes.TYPE_SM && renderGap('x05')}
      {renderGap('x2line')}
      {hasBrands && renderBrandCheckboxes()}
      {hasBoth && renderGap(type === LayoutStyleTypes.TYPE_SM ? 'x2line' : 'x1')}
      {hasCategories && renderCategoryCheckboxes()}
      {renderGap('x1')}
      {type === LayoutStyleTypes.TYPE_LG && renderClear(() => {
          area.applyClearButton(groupKey);
          Gtag.pushEvent("clear_filters_click", "");
        })}
    </div>
  );
};

export default FiltersElementMain;
